import { startOfDay } from "date-fns";
import type { TOptions } from "i18next";
import type { DefaultValues } from "react-hook-form";
import { RecurrenceEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { ReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import type {
	Reminder,
	ResourceKey,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { z } from "zod";

export const REMINDER_NAME_MAX_LENGTH = 50;

type TranslateFn = (key: ResourceKey, options?: TOptions) => string;

const getReminderBaseShape = (t: TranslateFn) => ({
	typeKey: z.string().min(1),
	date: z.date({
		required_error: t(Resources.Reminders.Validations.dateRequired),
		invalid_type_error: t(Resources.Reminders.Validations.dateRequired),
	}),
	recurrence: z.nativeEnum(RecurrenceEnum, {
		required_error: t(Resources.Form.Validations.required),
	}),
});

const refineFutureDate =
	(t: TranslateFn, initialDate: Date | null) =>
	(data: { date: Date }, ctx: z.RefinementCtx) => {
		const dateChanged =
			!initialDate ||
			startOfDay(data.date).getTime() !== startOfDay(initialDate).getTime();

		if (
			dateChanged &&
			startOfDay(data.date).getTime() <= startOfDay(new Date()).getTime()
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["date"],
				message: t(Resources.Reminders.Validations.dateFuture),
			});
		}
	};

const getReminderNameShape = (t: TranslateFn) =>
	z
		.string({
			required_error: t(Resources.Reminders.Validations.nameRequired),
		})
		.min(1, t(Resources.Reminders.Validations.nameRequired))
		.max(REMINDER_NAME_MAX_LENGTH, t(Resources.Reminders.Validations.nameMax));

type ReminderFormConfig = Pick<
	ReminderEntityConfig,
	"showName" | "allowRecurrence"
>;

const DEFAULT_FORM_CONFIG: ReminderFormConfig = {
	showName: true,
	allowRecurrence: true,
};

const getOptionalReminderNameShape = (t: TranslateFn) =>
	z
		.string()
		.max(REMINDER_NAME_MAX_LENGTH, t(Resources.Reminders.Validations.nameMax))
		.optional();

const refineRecurrence =
	(t: TranslateFn, allowRecurrence: boolean) =>
	(data: { recurrence: RecurrenceEnum }, ctx: z.RefinementCtx) => {
		if (!allowRecurrence && data.recurrence !== RecurrenceEnum.Once) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["recurrence"],
				message: t(Resources.Reminders.Validations.recurrenceNotAllowed),
			});
		}
	};

export const getReminderFormSchema = (
	t: TranslateFn,
	initialDate: Date | null,
	config: ReminderFormConfig = DEFAULT_FORM_CONFIG,
) => {
	const { typeKey, date, recurrence } = getReminderBaseShape(t);

	return z
		.object({
			typeKey,
			name: config.showName
				? getReminderNameShape(t)
				: getOptionalReminderNameShape(t),
			date,
			recurrence,
		})
		.superRefine((data, ctx) => {
			refineFutureDate(t, initialDate)(data, ctx);
			refineRecurrence(t, config.allowRecurrence)(data, ctx);
		});
};

export type ReminderFormModel = z.infer<
	ReturnType<typeof getReminderFormSchema>
>;

export const REMINDER_FORM_DEFAULT_VALUES: DefaultValues<ReminderFormModel> = {
	// The custom typeKey differs per entity (portfolio_custom / property_custom /
	// vehicle_custom), so it is filled in from GET /v2/reminders/types.
	typeKey: "",
	name: "",
	date: undefined,
	recurrence: RecurrenceEnum.Yearly,
};

export const getReminderFormDefaultValues = (
	config: ReminderFormConfig = DEFAULT_FORM_CONFIG,
): DefaultValues<ReminderFormModel> => ({
	...REMINDER_FORM_DEFAULT_VALUES,
	name: config.showName ? REMINDER_FORM_DEFAULT_VALUES.name : undefined,
	recurrence: config.allowRecurrence
		? REMINDER_FORM_DEFAULT_VALUES.recurrence
		: RecurrenceEnum.Once,
});

export const mapReminderToFormModel = (
	reminder: Reminder,
	config: ReminderFormConfig = DEFAULT_FORM_CONFIG,
): ReminderFormModel => ({
	typeKey: reminder.typeKey,
	...(config.showName && { name: reminder.name }),
	date: reminder.date,
	recurrence: config.allowRecurrence
		? reminder.recurrence
		: RecurrenceEnum.Once,
});
